import { ReactNode } from 'react';
import { Box, Text, Link } from '@nimbus-ds/components';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';

interface ChatMarkdownParserProps {
  content: string;
}

/**
 * Markdown parser for chat messages using react-markdown with Nimbus design system styling
 * Provides consistent formatting for markdown content in chat bubbles
 */
export function ChatMarkdownParser({
  content,
}: Readonly<ChatMarkdownParserProps>): JSX.Element {
  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => (
          <Box marginBottom="2">
            <Text fontWeight="bold" color="neutral-textHigh">
              {children}
            </Text>
          </Box>
        ),
        b: ({ children }) => (
          <Text fontWeight="medium" color="neutral-textHigh" as="span">
            {children as ReactNode}
          </Text>
        ),
        h2: ({ children }) => (
          <Box marginBottom="2">
            <Text
              fontSize="highlight"
              fontWeight="bold"
              color="neutral-textHigh"
            >
              {children as ReactNode}
            </Text>
          </Box>
        ),
        h3: ({ children }) => (
          <Box marginBottom="2">
            <Text fontSize="base" fontWeight="bold" color="neutral-textHigh">
              {children as ReactNode}
            </Text>
          </Box>
        ),
        p: ({ children }) => (
          <Box mb="3">
            <Text fontSize="base" color="neutral-textLow">
              {children as ReactNode}
            </Text>
          </Box>
        ),
        ul: ({ children }) => (
          <Box paddingLeft="4" marginBottom="3">
            <ul>{children as ReactNode}</ul>
          </Box>
        ),
        ol: ({ children }) => (
          <Box paddingLeft="4" marginBottom="3">
            <ol>{children as ReactNode}</ol>
          </Box>
        ),
        li: ({ children }) => (
          <Box marginBottom="1">
            <Text fontSize="base">{children as ReactNode}</Text>
          </Box>
        ),
        a: ({ href, children }) => {
          const safeHref = href ? defaultUrlTransform(href) : '';
          return safeHref ? (
            <Link
              as="a"
              href={safeHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {children as ReactNode}
            </Link>
          ) : (
            <span>{children}</span>
          );
        },
        strong: ({ children }) => (
          <Text fontWeight="medium" color="neutral-textHigh" as="span">
            {children as ReactNode}
          </Text>
        ),
        em: ({ children }) => <Text>{children as ReactNode}</Text>,
        blockquote: ({ children }) => (
          <Box
            paddingLeft="4"
            borderColor="neutral-surfaceHighlight"
            borderStyle="solid"
            borderWidth="1"
            marginBottom="3"
            marginTop="3"
          >
            <Text fontSize="base" color="neutral-textLow">
              {children as ReactNode}
            </Text>
          </Box>
        ),
        img: ({ src, alt }) => {
          const safeSrc = src ? defaultUrlTransform(src) : '';
          return safeSrc ? (
            <Box marginBottom="2" maxWidth="200px">
              <img src={safeSrc} alt={alt} width="100%" height="auto" />
            </Box>
          ) : null;
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

export default ChatMarkdownParser;
